/**
 * Phase 22 Plan 22-03 — Typed wrappers around the /api/clients endpoints.
 *
 * Mirrors services/sites.ts pattern: fetch via axios client, throw ApiError on non-2xx.
 */

import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type {
  ClientDto,
  ClientListItemDto,
  ClientListQuery,
  CreateClientBody,
  UpdateClientBody,
} from '@/types/client';
import type { PaginatedResult } from '@/types/roles';

function buildListUrl(q: ClientListQuery): string {
  const params = new URLSearchParams();
  if (q.q !== undefined && q.q !== '') params.set('q', q.q);
  if (q.status !== undefined) params.set('status', q.status);
  params.set('page', String(q.page ?? 1));
  params.set('pageSize', String(q.pageSize ?? 20));
  return `/api/clients?${params.toString()}`;
}

export async function listClientsRequest(
  query: ClientListQuery = {},
): Promise<PaginatedResult<ClientListItemDto>> {
  try {
    const { data } = await api.get<PaginatedResult<ClientListItemDto>>(buildListUrl(query));
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getClientRequest(id: number): Promise<ClientDto> {
  try {
    const { data } = await api.get<ClientDto>(`/api/clients/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function createClientRequest(body: CreateClientBody): Promise<ClientDto> {
  try {
    const { data } = await api.post<ClientDto>('/api/clients', body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function updateClientRequest(id: number, body: UpdateClientBody): Promise<ClientDto> {
  try {
    const { data } = await api.put<ClientDto>(`/api/clients/${String(id)}`, body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function deactivateClientRequest(id: number): Promise<void> {
  try {
    await api.post(`/api/clients/${String(id)}/deactivate`);
  } catch (e) {
    throw toApiError(e);
  }
}

export async function reactivateClientRequest(id: number): Promise<void> {
  try {
    await api.post(`/api/clients/${String(id)}/reactivate`);
  } catch (e) {
    throw toApiError(e);
  }
}
